/***************
 * Date Formatting - Using Intl.DateTimeFormat.
 ***************/

let cachedLocale;
export function getLocale(noCache = false) {
	if (!cachedLocale || noCache) {
		cachedLocale = document.documentElement.getAttribute('data-lang') || document.documentElement.getAttribute('lang') || "en-US";
	}

	return cachedLocale;
}

/**
 * Capitalize the first letter of a string, while respecting the locale
 * @param {string} value
 * @return {string}
 */
function capitalizeFirstLetter(value) {
	return value.charAt(0).toLocaleUpperCase(getLocale()) + value.slice(1);
}

/**
 *
 * @param {Date|string} date
 * @return {number|Date}
 */
function parseDate(date) {
	return typeof date === "string" ? new Date(date) : date;
}

/**
 * Format a date in a fixed short date format, based on the locale
 * @example 5 Nov 2021
 * @param {Date|string} date
 * @param {string=} locale
 * @param {string=} timeZone
 * @return {string}
 */
export function formatShortDate(
	date,
	locale = getLocale(),
	timeZone = undefined
) {
	const formatter = new Intl.DateTimeFormat(locale, {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone,
	});

	return formatter.format(parseDate(date));
}

/**
 * Format a date in a fixed long date format, based on the locale
 * @example Friday, 24 December 2021
 * @param {Date|string} date
 * @param {string=} locale
 * @param {string=} timeZone
 * @return {string}
 */
export function formatLongDate(
	date,
	locale = getLocale(),
	timeZone = undefined
) {
	const formatter = new Intl.DateTimeFormat(locale, {
		day: "numeric",
		month: "long",
		year: "numeric",
		weekday: "long",
		timeZone,
	});

	return capitalizeFirstLetter(
		// Since we most likely want to present the date standalone, we should have it capitalized. Otherwise the weekday might be presented in lowercase
		formatter.format(parseDate(date))
	);
}

/**
 * Format a date to time
 * @example 4:00 am
 * @param {Date|string} date
 * @param {string=} locale
 * @param {string=} timeZone
 * @return {string}
 */
export function formatTime(date, locale = getLocale(), timeZone = undefined) {
	const formatter = new Intl.DateTimeFormat(locale, {
		hour: "numeric",
		minute: "2-digit",
		timeZone,
	});

	return formatter.format(parseDate(date));
}

/**
 * Format a date with a custom set of DateTimeFormatOptions.
 * This allows you to fully customize the look of the result
 * @param {Date|string} date
 * @param {DateTimeFormatOptions} options
 * @param {string=} locale
 * @return {string}
 */
export function formatCustomDate(date, options, locale = getLocale()) {
	const formatter = new Intl.DateTimeFormat(locale, options);

	return formatter.format(parseDate(date));
}
